import { format } from "date-fns";

// Shared by the Quote History page (pages/rfq/QuoteHistory.tsx) and the
// RFQ list's quote history sidebar (QuoteHistorySidebar.tsx).

// Define interface for quote history item
export interface QuoteHistoryItem {
  rfq_id: number;
  customer_name: string;
  customer_email: string;
  ship_name: string;
  port_name: string;
  sent_at: string;
  sent_by: string;
  status: string;
  created_at: string;
  items_count: number;
  total_amount: number;
  items: {
    rfq_item_id: number;
    item_name: string;
    model_number: string;
    quantity: number;
    quoted_price: number;
    total_price: number;
    indent_days: number;
    order_index?: number | null;
  }[];
}

export interface QuoteHistoryFilterState {
  customerEmail: string;
  rfqId: number | "";
  startDate: Date | null;
  endDate: Date | null;
  keyword: string;
}

/** What getAllQuoteHistory is asked for: blank fields are left out, text is trimmed. */
export function buildQuoteHistoryFilters({ customerEmail, rfqId, startDate, endDate, keyword }: QuoteHistoryFilterState) {
  const filters: { customerEmail?: string; rfqId?: number; startDate?: string; endDate?: string; keyword?: string } = {};
  if (customerEmail.trim()) filters.customerEmail = customerEmail.trim();
  if (rfqId !== "") filters.rfqId = Number(rfqId);
  if (startDate) filters.startDate = format(startDate, "yyyy-MM-dd");
  if (endDate) filters.endDate = format(endDate, "yyyy-MM-dd");
  if (keyword.trim()) filters.keyword = keyword.trim();
  return filters;
}

/**
 * One entry per quoted RFQ, ordered by RFQ id. Every entry is its own quote:
 * getAllQuoteHistory reads one row per RFQ and sets quote_group_id to that
 * RFQ's id (as do the backend's quote-history producers), so the old
 * group-by-quote_group_id step never put two entries in one group.
 */
export function sortQuotesByRfqId(
  history: readonly (QuoteHistoryItem | null | undefined)[] | undefined,
  order: "asc" | "desc",
): QuoteHistoryItem[] {
  const sign = order === "asc" ? 1 : -1;
  return (history ?? [])
    .filter((item): item is QuoteHistoryItem => Boolean(item))
    .sort((a, b) => sign * ((a.rfq_id ?? 0) - (b.rfq_id ?? 0)));
}

// The display date of a quote. `item.sent_at` is read
// inside the try, as it was when this lived inline in each component.
export function formatQuoteDate(item: Pick<QuoteHistoryItem, "sent_at">): string {
  try {
    if (!item.sent_at) return "No date";
    const date = new Date(item.sent_at);
    if (isNaN(date.getTime())) return "Invalid date";
    return format(date, "MMM d, yyyy");
  } catch (e) {
    console.warn("Error formatting date:", e);
    return "Invalid date";
  }
}
